/**
 * POST /api/company/{id} payload helpers — align with Angular `editCompany()` (flat form fields only).
 * GET /api/company/:id returns nested relations; sending them back as JSON often causes 400 validation errors.
 *
 * Migration QA: `docs/COMPANY_MODULE_MIGRATION_README.md` → **Cross-cutting — POST payload QA** (Vitest + DEV diff).
 */

import {
  COMPANY_POST_ANGULAR_MULTISELECT_KEYS,
  COMPANY_POST_EXCLUDED_TOP_LEVEL_KEYS,
} from '../constants'

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date)
}

function isArrayOfObjects(arr: unknown[]): boolean {
  return arr.length > 0 && typeof arr[0] === 'object' && arr[0] !== null && !Array.isArray(arr[0])
}

/**
 * Remove nested relations and invalid keys Laravel rejects on update.
 */
export function sanitizeCompanyPostPayload(input: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, val] of Object.entries(input)) {
    if (COMPANY_POST_EXCLUDED_TOP_LEVEL_KEYS.has(key)) continue
    if (val === undefined) continue
    if (Array.isArray(val) && isArrayOfObjects(val)) continue
    if (isPlainObject(val)) continue
    out[key] = val
  }
  return out
}

/** Coerce Angular-style 0/1/"0"/"1" and JSON booleans to a real boolean. */
function coerceBooleanLike(v: unknown): boolean {
  if (v === true || v === 1 || v === '1' || v === 'true' || v === 'on' || v === 'yes') return true
  if (v === false || v === 0 || v === '0' || v === 'false' || v === 'no' || v === '' || v === null || v === undefined) {
    return false
  }
  return Boolean(v)
}

/**
 * Normalize types before POST /api/company/:id (sanitized flat object).
 * - `is_mailing_address`: `'1' | '0'` strings (matches Angular `editCompany()` + FormData).
 * - `check_exist`: boolean (FormData serializes as `"true"` / `"false"` like Angular).
 * - `federal_id`: always a string (never null) so PHP helpers never receive null.
 * - Drops keys whose value is still `null`.
 */
export function normalizeCompanyApiTypes(body: Record<string, unknown>): Record<string, unknown> {
  const out = { ...body }
  if ('is_mailing_address' in out) {
    out.is_mailing_address = coerceBooleanLike(out.is_mailing_address) ? '1' : '0'
  }
  if ('check_exist' in out) {
    out.check_exist = coerceBooleanLike(out.check_exist)
  }
  if ('federal_id' in out) {
    const f = out.federal_id
    if (f === null || f === undefined) {
      out.federal_id = ''
    } else if (typeof f === 'string') {
      out.federal_id = f.trim()
    } else {
      out.federal_id = String(f)
    }
  }
  const noNulls: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(out)) {
    if (v !== null) noNulls[k] = v
  }
  return noNulls
}

/**
 * Final flat object for POST /api/company/:id (pass to {@link companyUpdateBodyToFormData}).
 */
export function prepareCompanyUpdateBody(payload: Record<string, unknown>): Record<string, unknown> {
  return normalizeCompanyApiTypes(sanitizeCompanyPostPayload(payload))
}

/**
 * Encode like Angular `HttpService.post(..., { upload: true })`: multipart FormData with
 * `multiSelect`-style keys for known array fields.
 */
export function companyUpdateBodyToFormData(body: Record<string, unknown>): FormData {
  const fd = new FormData()
  for (const [key, val] of Object.entries(body)) {
    if (val === undefined || val === null) continue

    if (Array.isArray(val)) {
      if (COMPANY_POST_ANGULAR_MULTISELECT_KEYS.has(key)) {
        if (val.length > 0) {
          val.forEach((item, index) => {
            const s =
              item == null ? '' : typeof item === 'object' ? JSON.stringify(item) : String(item)
            fd.append(`${key}[${index}]`, s)
          })
        } else if (key !== 'listed_ids') {
          // Angular: empty non–listed_ids arrays become `key[]` with empty value
          fd.append(`${key}[]`, '')
        }
        continue
      }
      val.forEach((item, index) => {
        fd.append(`${key}[${index}]`, item == null ? '' : String(item))
      })
      continue
    }

    if (typeof val === 'boolean') {
      fd.append(key, val ? 'true' : 'false')
      continue
    }

    fd.append(key, String(val))
  }
  return fd
}

function sortKeysDeep(value: unknown): unknown {
  if (value === null || typeof value !== 'object') return value
  if (Array.isArray(value)) return value.map(sortKeysDeep)
  const obj = value as Record<string, unknown>
  const sorted: Record<string, unknown> = {}
  for (const key of Object.keys(obj).sort()) {
    sorted[key] = sortKeysDeep(obj[key])
  }
  return sorted
}

/**
 * Pretty JSON with recursively sorted keys — copy/paste next to Angular DevTools → Network →
 * `POST /api/company/:id` (parse FormData entries into a flat object first) for migration QA.
 */
export function stableStringifyForAngularPostDiff(body: Record<string, unknown>): string {
  return JSON.stringify(sortKeysDeep(body), null, 2)
}

export function logCompanyPostPayloadDev(companyId: string | undefined, body: Record<string, unknown>): void {
  if (import.meta.env.DEV) {
    console.log(`[POST /api/company/${companyId}]`, body)
    console.log(
      `[POST /api/company/${companyId}] stable JSON (E2E vs Angular editCompany payload — same inputs):`,
      stableStringifyForAngularPostDiff(body),
    )
  }
}
